import { IconButton, Link as MaterialLink } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import { useMemo, SyntheticEvent } from 'react';
import { useLocation } from 'react-router';
import { Mentor } from '../../api/mentors';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { getStatusVariant } from '../../utils/status';
import { ConfirmButton, StatusBadge, Text, Link } from '../common';

interface MentorsTableProps {
  mentors: Mentor[];
  disabledMentorsIds?: {
    [x: string]: boolean;
  };
  onRemove: (mentors: Mentor) => any;
  showTitle?: boolean;
  fullWidth?: boolean;
  readOnly?: boolean;
  isLink?: boolean;
}

const useStyles = makeStyles({
  table: {
    width: '100%',
    maxWidth: 480,
  },
  title: {
    width: '100%',
    padding: '14px 0',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr max-content',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '12px 0',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  name: {
    '& *': {
      maxWidth: 'max-content',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  fullWidth: {
    flexGrow: 1,
    maxWidth: 'none',
  },
  linkText: {
    color: COLORS.COLOR_BLUE_DARKENED_10,
    wordWrap: 'break-word',
  },
  text: {
    wordWrap: 'break-word',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
});

function MentorsTable({
  mentors,
  disabledMentorsIds,
  onRemove,
  showTitle = true,
  fullWidth = false,
  readOnly = false,
  isLink = false,
}: MentorsTableProps) {
  const classes = useStyles();
  const location = useLocation();
  const { rb } = useResourceBundles();
  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location],
  );

  const handleRemove = (mentor: Mentor) => () => {
    onRemove(mentor);
  };

  const stopPropagation = (e: SyntheticEvent<any>) => {
    e.stopPropagation();
  };

  return (
    <div
      className={cn(classes.table, {
        [classes.fullWidth]: fullWidth,
      })}>
      {showTitle && (
        <div className={classes.title}>
          <Text variant='normal2' bold>
            {rb('mentors-u')}
          </Text>
        </div>
      )}
      {mentors.map((mentor, index) => {
        const isDisabled = disabledMentorsIds
          ? disabledMentorsIds[mentor.id]
          : false;

        return (
          <div
            key={mentor.id}
            className={cn(classes.row, { [classes.fullWidth]: fullWidth })}
            data-testid={`assigned-mentor-${index}`}>
            <div className={classes.name}>
              {isLink ? (
                <MaterialLink
                  onClick={stopPropagation}
                  component={Link}
                  to={{
                    pathname: getRoutePath(Pages.MENTOR_DETAILS, {
                      mentorId: mentor.id,
                    }),
                    state: {
                      prevPath: currentPath,
                    },
                  }}>
                  <Text variant='normal' className={classes.linkText}>
                    {`${mentor.firstName} ${mentor.lastName}`}
                  </Text>
                </MaterialLink>
              ) : (
                <Text variant='normal' className={classes.text}>
                  {`${mentor.firstName} ${mentor.lastName}`}
                </Text>
              )}
            </div>
            {(!!mentor.assignmentStatus || !readOnly) && (
              <div className={classes.actions}>
                {!!mentor.assignmentStatus && (
                  <StatusBadge
                    status={mentor.assignmentStatus}
                    variant={getStatusVariant(mentor.assignmentStatus)}
                  />
                )}
                {!readOnly && (
                  <ConfirmButton
                    title='Remove the assignment?'
                    body='Sure you want to remove the assignment?'
                    successProps={{
                      btnLabel: 'Remove',
                      onSuccess: handleRemove(mentor),
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <IconButton
                        onClick={onClick}
                        disabled={isDisabled}
                        data-testid={`assigned-delete-button-${index}`}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default MentorsTable;
